import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { toast } from "react-toastify"
import SearchBar from "./_SearchBar"
import Filters from "./_Filters"
import List from "./_List"
import { useLocation } from "react-router-dom"
import { IGetConsultantsMaladiesListingRequestModel, IGetConsultantsMaladiesResponseModel, IListingContextPropsModel, IListItemInPreviewModel } from "./core/_models"
import ListPreview from "./_ListPreview"
import { getConsultantsMaladiesListingRequest } from "./core/_requests"
import { IIsBulkSelectionEnabledPropsModel } from "../../../../../models"

const REACT_QUERY_KEY_GET_LISTING = "REACT_QUERY_KEY_GET_LISTING"
const ListingContext = createContext<IListingContextPropsModel>({} as IListingContextPropsModel)
export const useListingContext = () => useContext(ListingContext)
const ConsultantsMaladiesListingMultiple = ({ title, isFilterIconVisible }: IDdcListingPropsModel) => {
     // Page states & URL parameters
     const location = useLocation() as {
          state: {
               keyword?: string
               dateDebut?: string
               dateFin?: string
          }
     }
     const urlParams = new URLSearchParams(window.location.search)
     const urlParamsDateDebut = urlParams.get("dateDebut") as string | null
     const urlParamsDateFin = urlParams.get("dateFin") as string | null
     const urlParamsKeyword = urlParams.get("keyword") as string | null

     // Referencing filters form, simple search bar form & preview container
     const filtersFormikRef = useRef<any>(null)
     const simpleSearchBarFormikRef = useRef<any>(null)
     const previewContainerRef = useRef<any>(null)

     // Filters, item in preview & bulk selection
     const [filters, setFilters] = useState<IGetConsultantsMaladiesListingRequestModel>({
          keyword: location.state?.keyword || urlParamsKeyword || undefined,
          dateDebut: location.state?.dateDebut || urlParamsDateDebut || undefined,
          dateFin: location.state?.dateFin || urlParamsDateFin || undefined,
          page: 1,
          pageLength: 20,
     })
     const [areFiltersVisible, setAreFiltersVisible] = useState<boolean>(false)
     const [itemInPreview, setItemInPreview] = useState<IListItemInPreviewModel | null>(null)
     const [isBulkSelectionEnabled, setIsBulkSelectionEnabled] = useState<IIsBulkSelectionEnabledPropsModel>({
          enabled: false,
          checkedElementsInPage: [],
          areAllElementsInAllPagesChecked: false,
     })

     // Main listing query
     const listingQuery = useQuery<IGetConsultantsMaladiesResponseModel, AxiosError>(REACT_QUERY_KEY_GET_LISTING, async () => {
          return getConsultantsMaladiesListingRequest(filters)
               .then(r => {
                    // Stop loaders (filters + simple search bar)
                    filtersFormikRef.current.setSubmitting(false)
                    simpleSearchBarFormikRef.current.setSubmitting(false)

                    return r.data
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors in the filters form
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) filtersFormikRef.current.setFieldError(key, error.errors[key])
                    }

                    // Stop loaders (filters + simple search bar)
                    filtersFormikRef.current.setSubmitting(false)
                    simpleSearchBarFormikRef.current.setSubmitting(false)

                    // Toast error
                    toast.error(error?.detail, { autoClose: false })

                    throw e
               })
     })

     useEffect(() => {
          listingQuery.refetch().then()
     }, [filters])

     return (
          <>
               <ListingContext.Provider
                    value={{
                         areFiltersVisible,
                         setAreFiltersVisible,
                         filters,
                         setFilters,
                         itemInPreview,
                         setItemInPreview,
                         REACT_QUERY_KEY_GET_LISTING,
                         listingQuery,
                         filtersFormikRef,
                         previewContainerRef,
                         simpleSearchBarFormikRef,
                         isBulkSelectionEnabled,
                         setIsBulkSelectionEnabled,
                         title,
                         isFilterIconVisible,
                    }}
               >
                    {/* Adapt view to display */}
                    {window.innerWidth >= 1200 ? <ViewXlOrHigher /> : <ViewLgOrLess />}
               </ListingContext.Provider>
          </>
     )
}

// View display Lg or less
const ViewLgOrLess = () => {
     const context = useListingContext()

     return (
          <>
               <div className={`${context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <SearchBar />
               </div>

               {context.itemInPreview?.selectedItem.arret.justificatifGoogleDriveID && context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                    <div className={"mb-2"}>
                         <ListPreview />
                    </div>
               )}

               <div className={`${!context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <Filters />
               </div>

               <List />
          </>
     )
}

// View display Xl or higher
const ViewXlOrHigher = () => {
     const context = useListingContext()
     return (
          <div className={"row"}>
               <div className={`col-5 ${!context.areFiltersVisible ? "d-none" : ""}`}>
                    <Filters />
               </div>
               <div className={`col ${context.itemInPreview && context.itemInPreview.isPreviewVisible ? "" : "col-7"}`}>
                    <div className={"row"}>
                         <div className={context.itemInPreview && context.itemInPreview.isPreviewVisible ? "col-5" : ""}>
                              {/* Simple bar search will be shown only if filters are not displayed */}
                              <div className={`mb-2 ${context.areFiltersVisible ? "d-none" : ""}`}>
                                   <SearchBar />
                              </div>

                              <List />
                         </div>
                         {context.itemInPreview?.selectedItem.arret.justificatifGoogleDriveID && context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                              <div className="col-7">
                                   <ListPreview />
                              </div>
                         )}
                    </div>
               </div>
          </div>
     )
}

interface IDdcListingPropsModel {
     title: string
     isFilterIconVisible: boolean
}

export default ConsultantsMaladiesListingMultiple
